/**
 * @file Sheet.tsx
 * @brief Side panel primitives (Radix Dialog — slide-over).
 *
 * @details
 * Same accessibility model as Dialog, but docks to an edge (default right)
 * for dense “peek while list stays visible” flows. Used by session detail.
 *
 * @dependencies
 * - @radix-ui/react-dialog
 * - lucide-react (X icon)
 * - shared/theme/cn
 * - shared/utils/restoreBodyInteraction
 *
 * @author DES
 * @date 29-07-2026
 */

import { PortalContainerProvider } from "@/components/ui/portalContainer";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from "react";
import { cn } from "@/shared/theme/cn";
import {
  keepOpenWhileMenuUp,
  restoreBodyInteraction,
} from "@/shared/utils/restoreBodyInteraction";

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetPortal = DialogPrimitive.Portal;
export const SheetClose = DialogPrimitive.Close;

export const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      "data-[state=closed]:pointer-events-none",
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

type SheetSide = "right" | "left" | "bottom" | "palette";

type SheetContentProps = ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  side?: SheetSide;
};

const sideClasses: Record<SheetSide, string> = {
  right:
    "inset-y-0 right-0 h-full w-full border-l data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right sm:max-w-xl lg:max-w-2xl",
  left: "inset-y-0 left-0 h-full w-full border-r data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left sm:max-w-xl lg:max-w-2xl",
  // The phone's action sheet (30-09-2026): up from the bottom, as tall as
  // its content, rounded on top, a phone's width. It sits on the right, 24px
  // inside the page rail's right grid line (the rail is 80rem, centred; see
  // shell-rail.ts), never closer than 16px to the window edge (DES: "move it
  // to the right further near the grid line but before").
  bottom:
    "bottom-0 right-[max(1rem,calc((100vw-80rem)/2+1.5rem))] max-h-[85dvh] w-[calc(100vw-2rem)] max-w-[400px] rounded-t-[28px] border-t data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
  // The Cmd+K / Ask frame (DES 01-10-2026, Edit user: "slide ki bajaye cmd
  // k ki tarha karo"): drops from under the 56px topbar across the page
  // rail (80rem, centred), as tall as its content, a small fade and drop
  // instead of a slide from the side.
  palette:
    "inset-x-0 top-14 mx-auto max-h-[calc(100dvh-4.5rem)] w-full max-w-[80rem] border-b data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-2 data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2",
};

export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(
  (
    { className, children, side = "right", onCloseAutoFocus, onPointerDownOutside, ...props },
    ref,
  ) => (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50 flex flex-col gap-0 bg-background shadow-xl outline-none",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:duration-300 data-[state=closed]:duration-200",
          "data-[state=closed]:pointer-events-none",
          sideClasses[side],
          className,
        )}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          event.preventDefault();
          restoreBodyInteraction();
        }}
        onPointerDownOutside={(event) => {
          // A press that only closes an open menu must not close the form.
          if (keepOpenWhileMenuUp(event)) return;
          onPointerDownOutside?.(event);
        }}
        {...props}
      >
        {/* Menus opened inside the sheet portal in here, inside its focus
            trap (portalContainer.tsx). */}
        <PortalContainerProvider>{children}</PortalContainerProvider>
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-none opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </SheetPortal>
  ),
);
SheetContent.displayName = "SheetContent";

export function SheetHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 border-b px-6 pb-4 pr-12 pt-6 text-left",
        className,
      )}
      {...props}
    />
  );
}

export function SheetFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "mt-auto flex flex-wrap items-center justify-end gap-2 border-t px-6 py-4",
        className,
      )}
      {...props}
    />
  );
}

export const SheetTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

export const SheetDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";
